const cityAliases = Object.freeze({ 'Asia/Calcutta': 'Kolkata', 'Europe/Kiev': 'Kyiv' });
const normalize = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[_/]/g, ' ').trim();

export function timezoneOptions(zones, referenceDate = new Date()) {
  return [...new Set(zones)].sort().map(value => {
    const offset = new Intl.DateTimeFormat('en-US', { timeZone: value, timeZoneName: 'longOffset' }).formatToParts(referenceDate).find(part => part.type === 'timeZoneName').value;
    return { value, label: value.replaceAll('_', ' '), offset: offset === 'GMT' ? 'UTC+00:00' : offset.replace('GMT', 'UTC'), alias: cityAliases[value] ?? '' };
  });
}

export function filterTimezones(options, query) {
  const search = normalize(query), terms = search.split(/\s+/).filter(Boolean);
  const result = options.filter(option => terms.every(term => normalize(`${option.value} ${option.label} ${option.offset} ${option.alias}`).includes(term)));
  // An exact IANA identifier stays ahead of matches in the UTC offset.
  return result.sort((a, b) => Number(normalize(b.value) === search) - Number(normalize(a.value) === search));
}

export function createTimezonePicker(dialog, { zones, initialValue, referenceDate, onChange }) {
  const find = name => dialog.querySelector(`[data-booking-timezone-${name}]`);
  const trigger = find('toggle'), current = find('current'), popup = find('popup'), search = find('search'), list = find('options'), empty = find('empty');
  const valueInput = dialog.querySelector('[data-booking-timezone]');
  let value = initialValue, options = [], matches = [], active = -1, referenceKey = '';
  valueInput.value = value; current.textContent = value.replaceAll('_', ' ');

  function position() {
    if (popup.hidden) return;
    const box = dialog.getBoundingClientRect(), field = trigger.getBoundingClientRect(), gap = 12;
    const width = Math.min(Math.max(field.width, 320), dialog.clientWidth - gap * 2);
    const left = Math.max(gap, Math.min(field.left - box.left, dialog.clientWidth - width - gap));
    const below = Math.max(0, dialog.clientHeight - (field.bottom - box.top) - gap * 2);
    const above = Math.max(0, field.top - box.top - gap * 2);
    const downward = below >= 200 || below >= above;
    const height = Math.min(352, Math.max(104, downward ? below : above), dialog.clientHeight - gap * 2);
    const top = Math.max(gap, Math.min(downward ? field.bottom - box.top + gap / 2 : field.top - box.top - height - gap / 2, dialog.clientHeight - height - gap));
    // These distances come from physical browser rectangles; the inline inset
    // converts them once to the popup's inherited writing direction.
    const start = getComputedStyle(dialog).direction === 'rtl' ? dialog.clientWidth - left - width : left;
    popup.style.insetInlineStart = `${start}px`; popup.style.insetBlockStart = `${top}px`;
    popup.style.inlineSize = `${width}px`; popup.style.blockSize = `${height}px`;
  }
  function highlight(index, scroll = true) {
    active = index;
    for (const [number, option] of [...list.children].entries()) {
      option.dataset.active = String(number === active);
      option.setAttribute('aria-selected', String(number === active));
    }
    const option = list.children[active];
    if (option) { search.setAttribute('aria-activedescendant', option.id); if (scroll) option.scrollIntoView({ block: 'nearest' }); }
    else search.removeAttribute('aria-activedescendant');
  }
  function render(query = '', selected = false) {
    matches = filterTimezones(options, query);
    const fragment = document.createDocumentFragment();
    for (const [index, entry] of matches.entries()) {
      const option = document.createElement('div'); option.setAttribute('role', 'option'); option.id = `booking-timezone-option-${index}`; option.dataset.zone = entry.value;
      const label = document.createElement('bdi'); label.dir = 'ltr'; label.textContent = entry.label;
      const offset = document.createElement('bdi'); offset.dir = 'ltr'; offset.className = 'an-booking__timezone-offset'; offset.textContent = entry.offset;
      option.append(label, offset); fragment.append(option);
    }
    list.replaceChildren(fragment); empty.hidden = !!matches.length; list.hidden = !matches.length;
    highlight(selected ? matches.findIndex(entry => entry.value === value) : -1, false);
  }
  function open() {
    const date = referenceDate(), key = date.toISOString().slice(0, 10);
    if (key !== referenceKey) { options = timezoneOptions(zones, date); referenceKey = key; }
    popup.hidden = false; trigger.setAttribute('aria-expanded', 'true'); search.setAttribute('aria-expanded', 'true');
    search.value = ''; render('', true); position(); search.focus({ preventScroll: true }); highlight(active);
  }
  function close({ focus = false } = {}) {
    popup.hidden = true; trigger.setAttribute('aria-expanded', 'false'); search.setAttribute('aria-expanded', 'false'); search.removeAttribute('aria-activedescendant');
    if (focus && dialog.open) trigger.focus({ preventScroll: true });
  }
  function choose(entry) {
    if (!entry) return;
    const changed = value !== entry.value;
    value = entry.value; valueInput.value = value; current.textContent = entry.label;
    close({ focus: true });
    if (changed) onChange(value);
  }
  trigger.addEventListener('click', () => popup.hidden ? open() : close({ focus: true }));
  trigger.addEventListener('keydown', event => { if (['ArrowDown', 'ArrowUp'].includes(event.key)) { event.preventDefault(); open(); } });
  search.addEventListener('input', () => { render(search.value); list.scrollTop = 0; });
  search.addEventListener('keydown', event => {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close({ focus: true }); }
    else if (event.key === 'Enter') { event.preventDefault(); choose(matches[active] ?? (matches.length === 1 ? matches[0] : null)); }
    else if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      if (matches.length) highlight(active < 0 ? (event.key === 'ArrowDown' ? 0 : matches.length - 1) : Math.max(0, Math.min(matches.length - 1, active + (event.key === 'ArrowDown' ? 1 : -1))));
    }
  });
  // Option focus stays on the search combobox, including mouse/touch selection.
  list.addEventListener('mousedown', event => { if (event.target.closest('[data-zone]')) event.preventDefault(); });
  list.addEventListener('click', event => { const option = event.target.closest('[data-zone]'); if (option) choose(matches.find(entry => entry.value === option.dataset.zone)); });
  document.addEventListener('pointerdown', event => { if (!popup.hidden && !popup.contains(event.target) && !trigger.contains(event.target)) close(); });
  dialog.addEventListener('focusin', event => { if (!popup.hidden && !popup.contains(event.target) && !trigger.contains(event.target)) close(); });
  dialog.addEventListener('cancel', event => { if (!popup.hidden) { event.preventDefault(); close({ focus: true }); } });
  dialog.querySelector('.an-booking__schedule-body').addEventListener('scroll', position, { passive: true });
  return Object.freeze({ close, position, get isOpen() { return !popup.hidden; } });
}
